import { Dispatch, ReactElement, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Option, isSome } from 'fp-ts/lib/Option';
import { fold as foldEither } from 'fp-ts/lib/Either';

import { ErrorData } from 'domain/types';
import { ConstraintsPerLayoutElement, LayoutType } from 'domain/entity/layout';
import { AnalysisPreset, Visualisation, AtomVisualisation, VISUALISATION_TYPE } from 'domain/entity/analysis';
import { AnalysisDataCriteriaBuilder } from 'use-case/analysis/analysisCriteria';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { useBranding } from 'adapter/in/common/context/static/Branding';
import { getWidgetBuilder } from 'adapter/in/common/widget/widgetBuilder';
import { WidgetOptionConfigurationField, WidgetType } from 'domain/entity/widget';
import TextData from 'domain/entity/widgets/TextData';
import { useAnalysisView } from 'adapter/in/advanced-analytics/context/AnalysisView';
import { AnalysisData } from 'domain/analysisData';
import { LiveAnalysisConfig } from 'adapter/in/advanced-analytics/hooks/useFormGenerator';
import { useUIRestrictions } from 'adapter/in/advanced-analytics/context/UIRestrictions';
import { useAdvancedAnalyticsDependencies } from 'adapter/in/advanced-analytics/advancedAnalyticsDependencies';
import { useLocation } from 'react-router-dom';
import { PeriodType } from 'adapter/in/common/components/PeriodSelector';
import { PreviewAnalysisConfig } from 'adapter/in/common/analysis/PreviewAnalysisConfig';
import { PreviewAnalysisData } from 'use-case/analysis/data/preview/previewAnalysisDataUseCase';

export enum CompareTimeshift {
    NO_SHIFT = 'NO_SHIFT',
    SHIFT_1_DURATION_BEFORE = 'SHIFT_1_DURATION_BEFORE',
    SHIFT_1_YEAR_BEFORE = 'SHIFT_1_YEAR_BEFORE',
    SHIFT_1_DURATION_BEFORE_WITH_WEEKDAY = 'SHIFT_1_DURATION_BEFORE_WITH_WEEKDAY',
    SHIFT_1_YEAR_BEFORE_WITH_WEEKDAY = 'SHIFT_1_YEAR_BEFORE_WITH_WEEKDAY',
    SHIFT_TO_PERIOD_BEFORE = 'SHIFT_1_PERIOD_BEFORE',
}

export type AnalysisConfiguration = {
    analysisId: string;
    resolution: string;
    compareTimeshift: CompareTimeshift;
    visualisation: Visualisation;
    atomsVisualisation: AtomVisualisation[];
    period: string;
    presets: AnalysisPreset[];
};

export type AnalysisDataError = Option<ErrorData>;
export type AnalysisDataResult = AnalysisData | null;

export const chartConfigKey = (analysisConfig: LiveAnalysisConfig): string => {
    if (analysisConfig === null) return 'null';
    const { resolution, period, compareTimeshift, presets, atomsVisualisation, visualisation } = analysisConfig;
    return JSON.stringify({
        resolution,
        period,
        compareTimeshift,
        presets,
        atomsVisualisation,
        visualisation: {
            type: visualisation.type,
            combinedSubtype: visualisation.combinedSubtype,
            stack: visualisation.stack,
            fillArea: visualisation.fillArea,
            indicatorSubtype: visualisation.indicatorSubtype,
            reducerOperation: visualisation.reducerOperation,
            accumulatorOperation: visualisation.accumulatorOperation,
            invertAxes: visualisation.invertAxes,
            magnitudePreferences: visualisation.magnitudePreferences,
        },
    });
};

type BuildCriteriaOptions = {
    shifts?: number;
    saveChanges?: boolean;
    visualisation: Visualisation;
};

export const buildAnalysisDataCriteria = (
    analysisConfig: LiveAnalysisConfig & object,
    analysisId: string,
    context: string | null,
    { shifts = 0, saveChanges = false, visualisation }: BuildCriteriaOptions
) => {
    const { resolution, period, compareTimeshift, presets, atomsVisualisation } = analysisConfig;
    return AnalysisDataCriteriaBuilder.create()
        .withAnalysisId(analysisId)
        .withResolution(resolution)
        .withPeriod(period === PeriodType.CONTEXT && context !== null ? context : period)
        .withVisualisation(visualisation)
        .withCompareTimeshiftAndAlignment(compareTimeshift)
        .withPresets([...presets].reverse())
        .withTimeShifts(shifts)
        .withAtomsVisualisation(atomsVisualisation)
        .withSaveChanges(saveChanges)
        .build();
};

export const useAnalysisDataUseCase = (
    analysisConfig: LiveAnalysisConfig
): {
    analysisData: AnalysisDataResult;
    analysisDataError: AnalysisDataError;
    previewAnalysisData: (previewAnalysisConfig?: PreviewAnalysisConfig) => void;
    analysisDataIsLoading: boolean;
    reset: () => void;
} => {
    const { previewAnalysisData: previewAnalysisDataUseCase } = useAdvancedAnalyticsDependencies();
    const { data, error, execute, reset, isLoading } = useCase(previewAnalysisDataUseCase, false);
    const { analysisId } = useAnalysisView();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const context = queryParams.get('context');

    const previewAnalysisData = ({ shifts = 0, asTimeSeries = false, saveChanges = false }: PreviewAnalysisConfig = {}) => {
        if (analysisConfig !== null) {
            execute(
                buildAnalysisDataCriteria(analysisConfig, analysisId, context, {
                    shifts,
                    saveChanges,
                    visualisation: asTimeSeries ? new Visualisation(VISUALISATION_TYPE.RAW) : analysisConfig.visualisation,
                })
            );
        } else {
            throw new Error('Trying to call null config');
        }
    };

    useEffect(() => {
        if (isSome(data)) {
            reset();
        }
    }, [chartConfigKey(analysisConfig)]);

    return {
        analysisData: isSome(data) ? data.value : null,
        analysisDataError: error,
        previewAnalysisData,
        reset,
        analysisDataIsLoading: isLoading,
    };
};

type AiTextGenerationState = {
    result: AnalysisData | null;
    isGenerating: boolean;
    hasError: boolean;
};

const INITIAL_AI_TEXT_GENERATION_STATE: AiTextGenerationState = { result: null, isGenerating: false, hasError: false };

export const useAiTextGeneration = (
    previewAnalysisData: PreviewAnalysisData
): {
    aiDescriptionText: string | null;
    isGenerating: boolean;
    hasError: boolean;
    hasResult: boolean;
    generate: (analysisConfig: LiveAnalysisConfig, analysisId: string, context: string | null, userPrompt: string) => void;
} => {
    const [state, setState] = useState<AiTextGenerationState>(INITIAL_AI_TEXT_GENERATION_STATE);
    // AI generation calls take seconds: a superseded in-flight request must not overwrite
    // a newer one (latest-wins), and a late response must not update an unmounted hook.
    const requestSeq = useRef(0);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const generate = useCallback(
        (analysisConfig: LiveAnalysisConfig, analysisId: string, context: string | null, userPrompt: string) => {
            if (analysisConfig === null) {
                return;
            }
            requestSeq.current += 1;
            const seq = requestSeq.current;
            setState({ result: null, isGenerating: true, hasError: false });
            previewAnalysisData(
                buildAnalysisDataCriteria(analysisConfig, analysisId, context, {
                    visualisation: Visualisation.aiGeneratedText(userPrompt),
                })
            ).then(
                foldEither(
                    () => {
                        if (isMounted.current && seq === requestSeq.current) {
                            setState({ result: null, isGenerating: false, hasError: true });
                        }
                    },
                    (data: AnalysisData) => {
                        if (isMounted.current && seq === requestSeq.current) {
                            setState({ result: data, isGenerating: false, hasError: false });
                        }
                    }
                )
            );
        },
        [previewAnalysisData]
    );

    const { result } = state;
    const aiDescriptionText = result && result.type === WidgetType.TEXT ? (result as TextData).body.text : null;

    return {
        aiDescriptionText,
        isGenerating: state.isGenerating,
        hasError: state.hasError,
        hasResult: result !== null,
        generate,
    };
};

export const useAnalysisBuilder = (
    referencer: Dispatch<SetStateAction<HTMLDivElement | null>>,
    analysisData: AnalysisDataResult,
    analysisDataError: AnalysisDataError
): {
    AnalysisBuilder: (
        width: number,
        height: number,
        layoutType?: LayoutType,
        isOnEdit?: boolean,
        sizeIssue?: ConstraintsPerLayoutElement
    ) => ReactElement;
} => {
    const { profile } = useProfile();
    const { branding } = useBranding();
    const { analysisTitle, analysisId } = useAnalysisView();
    const { editTitleTab } = useUIRestrictions();

    const cachedAnalysisData = useMemo(() => analysisData || null, [analysisData]);
    const getCachedAnalysisIfWidget = (analysis: AnalysisDataResult) =>
        analysis && analysis.type !== WidgetType.TIMESERIES ? analysis : {};

    const getTitle = () => {
        if (editTitleTab) {
            return window.location.pathname.indexOf('analysis') > -1 && analysisData && analysisData.type !== WidgetType.SINGLE_KPI
                ? [new WidgetOptionConfigurationField('title', analysisTitle)]
                : [];
        }
        return [new WidgetOptionConfigurationField('title', '')];
    };
    return {
        AnalysisBuilder: getWidgetBuilder(
            {
                id: analysisId,
                type: WidgetType.ANALYSIS,
                optionFields: getTitle(),
            },
            getCachedAnalysisIfWidget(cachedAnalysisData),
            false,
            branding,
            profile.decimalSeparator,
            profile.thousandsSeparator,
            profile.dateFormat,
            profile.currencySymbolInLastPosition,
            referencer,
            isSome(analysisDataError) ? analysisDataError.value : undefined
        ),
    };
};
